import {
  joinPositionText,
  POSITION_AXES,
  type PositionAxis,
  type PositionTextParts,
  padPositionDegrees,
  padPositionMinutes,
  splitPositionText,
} from '@felix/domain';
import { Input, NativeSelect, TextInput } from '@mantine/core';
import { type ReactNode, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classes from './PositionInput.module.css';

export interface PositionInputProps {
  axis: PositionAxis;
  label: string;
  /**
   * Kanonik metin (`34° 45.6' N`, `099° 12.6' E`) ya da boş. Eksik giriş ayrıştırılamayan metin
   * olarak gelir; form onu `INVALID_POSITION` ile gösterir (konum sessizce düşmez).
   */
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  required?: boolean;
  readOnly?: boolean;
  error?: ReactNode;
  description?: ReactNode;
  /** Derece kutusunun solundaki ikon (ör. harita iğnesi). */
  leftSection?: ReactNode;
  /** Kök (etiket ve kutular) sınıfı, ör. ızgarada satırın tamamı. */
  className?: string;
  classNames?: { label?: string; input?: string; required?: string; error?: string };
}

const DEGREES = /^\d*$/;
const MINUTES = /^\d{0,2}(?:[.,]\d?)?$/;

/**
 * Konum girişi (ADR 0054 §3): derece, dakika (bir ondalık, 0–59.9) ve yön ayrı kutular. Enlem
 * `DD° MM.m' N/S`, boylam `DDD° MM.m' E/W`; baştaki sıfırlar alandan çıkınca yazılır
 * [IMO Res. A.851(20) Ek C]. Değer metindir; dönüştürme ve doğrulama `@felix/domain`
 * (`parsePosition`). Saklama ondalık derecedir ve ekranın işidir.
 */
export function PositionInput({
  axis,
  label,
  value,
  onChange,
  onBlur,
  required,
  readOnly,
  error,
  description,
  leftSection,
  className,
  classNames,
}: PositionInputProps) {
  const { t } = useTranslation();
  const labelId = useId();
  const errorId = useId();
  const rule = POSITION_AXES[axis];
  const [parts, setParts] = useState<PositionTextParts>(() => splitPositionText(value, axis));
  // Son yazdığımız metin: dışarıdan gelen farklı değer (form sıfırlandı) kutuları yeniler
  const emitted = useRef(value);
  useEffect(() => {
    if (value === emitted.current) return;
    emitted.current = value;
    setParts(splitPositionText(value, axis));
  }, [value, axis]);

  const update = (next: PositionTextParts) => {
    setParts(next);
    const text = joinPositionText(next);
    if (text === emitted.current) return;
    emitted.current = text;
    onChange(text);
  };

  const name = (part: 'degrees' | 'minutes' | 'hemisphere') =>
    t(`common.position.${part}`, { label });
  const invalid = !!error;
  const inputClass = [classes.box, classNames?.input].filter(Boolean).join(' ');
  const common = {
    readOnly: !!readOnly,
    error: invalid,
    'aria-invalid': invalid,
    ...(invalid ? { 'aria-describedby': errorId } : {}),
    classNames: { input: inputClass, section: classes.unit ?? '' },
  };

  return (
    <Input.Wrapper
      {...(className ? { className } : {})}
      label={label}
      labelElement="div"
      id={labelId}
      withAsterisk={!!required && !readOnly}
      error={error}
      description={description}
      inputWrapperOrder={['label', 'input', 'description', 'error']}
      errorProps={{ id: errorId }}
      classNames={{
        label: classNames?.label ?? '',
        required: classNames?.required ?? '',
        error: classNames?.error ?? '',
      }}
      labelProps={{ id: `${labelId}-label` }}
    >
      <fieldset className={classes.row} aria-labelledby={`${labelId}-label`}>
        <TextInput
          {...common}
          aria-label={name('degrees')}
          inputMode="numeric"
          autoComplete="off"
          maxLength={rule.width}
          placeholder={'0'.repeat(rule.width)}
          value={parts.degrees}
          leftSection={leftSection}
          rightSection={<span aria-hidden>°</span>}
          rightSectionPointerEvents="none"
          rightSectionWidth={16}
          onChange={(event) => {
            const next = event.currentTarget.value;
            if (DEGREES.test(next)) update({ ...parts, degrees: next });
          }}
          onBlur={() => {
            const padded = padPositionDegrees(parts.degrees, axis);
            if (padded !== parts.degrees) update({ ...parts, degrees: padded });
            onBlur?.();
          }}
        />
        <TextInput
          {...common}
          aria-label={name('minutes')}
          inputMode="decimal"
          autoComplete="off"
          maxLength={4}
          placeholder="00.0"
          value={parts.minutes}
          rightSection={<span aria-hidden>'</span>}
          rightSectionPointerEvents="none"
          rightSectionWidth={16}
          onChange={(event) => {
            const next = event.currentTarget.value;
            if (MINUTES.test(next)) update({ ...parts, minutes: next });
          }}
          onBlur={() => {
            const padded = padPositionMinutes(parts.minutes);
            if (padded !== parts.minutes) update({ ...parts, minutes: padded });
            onBlur?.();
          }}
        />
        {readOnly ? (
          <TextInput {...common} aria-label={name('hemisphere')} value={parts.hemisphere} />
        ) : (
          <NativeSelect
            aria-label={name('hemisphere')}
            error={invalid}
            aria-invalid={invalid}
            data={[...rule.hemispheres]}
            rightSectionWidth={20}
            value={parts.hemisphere}
            classNames={{ input: inputClass }}
            onChange={(event) => update({ ...parts, hemisphere: event.currentTarget.value })}
            onBlur={() => onBlur?.()}
          />
        )}
      </fieldset>
    </Input.Wrapper>
  );
}
